/**
 * Global data export — builds a ZIP with CSVs for all stores.
 * Extracted from global-settings.jsx for reuse in profil-tab.
 */

function csvEscape(v) {
  if (v === null || v === undefined) return '';
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  if (/[",;\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function rowsToCsv(rows, columns) {
  if (!rows || rows.length === 0) return columns.join(',') + '\n';
  const header = columns.join(',');
  const body = rows.map(r => columns.map(c => csvEscape(r[c])).join(',')).join('\n');
  return header + '\n' + body + '\n';
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function fetchStoreCsvFiles(store, authToken) {
  const headers = { 'Authorization': `Bearer ${authToken}` };
  const apiBase = `/api/stores/${store.id}`;
  const files = {};
  const safeName = (store.name || 'store').replace(/[^a-z0-9]+/gi, '_');
  const prefix = `${safeName}__`;

  if ((store.type || 'online') === 'online') {
    try {
      const r = await fetch(`${apiBase}/orders?include_archived=1`, { headers });
      if (r.ok) {
        const orders = await r.json();
        files[`${prefix}orders.csv`] = rowsToCsv(orders, [
          'id', 'order_number', 'channel_id', 'buyer_name', 'status', 'price', 'shipping_cost', 'fees',
          'tracking_number', 'carrier', 'label_key', 'is_archived', 'archived_at', 'created_at', 'updated_at',
        ]);
      }
    } catch { /* skip */ }
    try {
      const r = await fetch(`${apiBase}/batches`, { headers });
      if (r.ok) {
        const batches = await r.json();
        files[`${prefix}batches.csv`] = rowsToCsv(batches, [
          'id', 'label', 'status', 'starts_at', 'ends_at', 'total_revenue', 'total_fees', 'created_at',
        ]);
      }
    } catch { /* skip */ }
  }

  if (store.type === 'flea') {
    try {
      const r = await fetch(`${apiBase}/items?include_inactive=1`, { headers });
      if (r.ok) {
        const items = await r.json();
        files[`${prefix}items.csv`] = rowsToCsv(items, [
          'id', 'name', 'description', 'price', 'wish_price', 'quantity', 'active', 'created_at', 'updated_at',
        ]);
      }
    } catch { /* skip */ }
    try {
      const r = await fetch(`${apiBase}/sessions?limit=2000`, { headers });
      if (r.ok) {
        const raw = await r.json();
        const sessions = Array.isArray(raw) ? raw : (raw.sessions || []);
        files[`${prefix}sessions.csv`] = rowsToCsv(sessions, [
          'id', 'location', 'starts_at', 'ends_at', 'notes', 'created_at',
        ]);
        const allSales = []; const allSaleItems = [];
        await Promise.all(sessions.map(async (s) => {
          try {
            const sr = await fetch(`${apiBase}/sessions/${s.id}`, { headers });
            if (!sr.ok) return;
            const detail = await sr.json();
            for (const sale of (detail.sales || [])) {
              allSales.push({ ...sale, _location: s.location });
              for (const si of (sale.sale_items || [])) {
                allSaleItems.push({ ...si, sale_id: sale.id, session_id: s.id });
              }
            }
          } catch { /* skip */ }
        }));
        if (allSales.length > 0) {
          files[`${prefix}sales.csv`] = rowsToCsv(allSales, [
            'id', 'session_id', '_location', 'total', 'payment', 'is_bundle', 'is_voided',
            'voided_at', 'sold_at', 'notes',
          ]);
        }
        if (allSaleItems.length > 0) {
          files[`${prefix}sale_items.csv`] = rowsToCsv(allSaleItems, [
            'id', 'sale_id', 'session_id', 'item_id', 'item_name', 'quantity', 'unit_price', 'wish_price',
          ]);
        }
      }
    } catch { /* skip */ }
  }

  return files;
}

export async function buildGlobalExportZip(stores, authToken) {
  if (!window.JSZip) {
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/jszip@3.10.1/dist/jszip.min.js';
      s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  const zip = new window.JSZip();
  zip.file('stores.csv', rowsToCsv(stores, ['id', 'name', 'type', 'owner_id', 'created_at', 'role']));
  for (const store of stores) {
    const files = await fetchStoreCsvFiles(store, authToken);
    for (const [name, content] of Object.entries(files)) {
      zip.file(name, content);
    }
  }
  const blob = await zip.generateAsync({ type: 'blob' });
  const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  downloadBlob(blob, `boothly-export-${ts}.zip`);
}
